import styled from 'styled-components';
import { palette } from '../../../theme/colors';
import ArrowRightImg from '../../../assets/icons/chevron-right-circle.svg';
import InfoCircleDark from '../../../assets/icons/exclamation-mark-circle--dark.svg';
import InfoCircleWhite from '../../../assets/icons/exclamation-mark-circle--white.svg';
import CloseWhite from '../../../assets/icons/cross--white.svg';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { translate } from '../../../utils/translate';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { convertSecondsToTime } from '../../../utils/date';
import isElectron from 'is-electron';
import type { LoneWorkerPersonalAlarmResponse } from '../../../containers/LoneWorkerTimer/loneWorkerTimerSlice/types';
import { timespanToSeconds } from '../../../containers/LoneWorkerTimer/helpers';
import {
  selectIsLwtLoading,
  setIsDeactivated,
} from '../../../containers/LoneWorkerTimer/loneWorkerTimerSlice';
import { localStorageSet } from '../../../utils/storage';
import { fetchAlarmData } from '../../../containers/LoneWorkerTimer/loneWorkerTimerSlice/actionCreator';

const dayInSeconds = 86399 as const;

const SContainer = styled.div<{ isDeactivated?: boolean }>`
  background-color: ${props => (props.isDeactivated ? palette.prussianBlue2 : palette.honeyYellow)};
  margin: 1rem 7% 0 7%;
  border-radius: 0.75rem;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  p {
    font-family: 'Roboto-Regular';
    font-weight: 400;
    font-size: 12px;
    line-height: 1rem;
    color: ${props => (props.isDeactivated ? palette.white : palette.raisinBlack3)};
  }
  .row {
    display: flex;
    align-items: center;
    p {
      padding-right: 10px;
      font-weight: 500;
    }
  }
  .alarmIcon {
    padding-right: 10px;
  }
  .learnMore {
    font-weight: 700;
    font-family: 'Roboto-Medium';
    cursor: pointer;
    text-decoration: none;
    outline: none;
    border: none;
    padding: 0;
    margin: 0;
    display: inline-block;
    background-color: transparent;

    &:hover {
      text-decoration: underline;
    }
    &:active {
      opacity: 0.8;
    }
  }
`;

type IPersonalTimerBannerProps = {
  className?: string | undefined;
  alarmData?: LoneWorkerPersonalAlarmResponse | null;
  isDeactivated?: boolean;
};

export const LoneWorkerTimerBanner = (props: IPersonalTimerBannerProps) => {
  const { className, alarmData, isDeactivated } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [seconds, setSeconds] = useState(() =>
    alarmData ? Math.round(Math.max(0, timespanToSeconds(alarmData.DispatchDelay))) : dayInSeconds
  );
  const isLoading = useAppSelector(selectIsLwtLoading);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  function setTimer() {
    if (alarmData) {
      const secondsLeft = Math.round(Math.max(0, timespanToSeconds(alarmData.DispatchDelay)));
      setSeconds(secondsLeft);
      createTimerInterval();
    }
  }

  useEffect(() => {
    return () => clearTimerInterval();
  }, []);

  const createTimerInterval = () => {
    clearTimerInterval();
    intervalRef.current = setInterval(() => {
      setSeconds(curSeconds => {
        const nextSeconds = Math.max(curSeconds - 1, 0);

        if (nextSeconds <= 0) {
          clearTimerInterval();
          dispatch(fetchAlarmData(navigate, true));
        }

        return nextSeconds;
      });
    }, 1000);
  };

  const clearTimerInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (alarmData && isElectron()) {
      localStorageSet('isLoneWorkTimerActive', JSON.stringify(true));
      setTimer();
    }
  }, [alarmData]);

  const onBannerClick = () => {
    if (!isDeactivated) {
      navigate('/aloneWorker/timer');
    }
  };

  const onCloseClick = () => {
    if (isDeactivated) {
      dispatch(setIsDeactivated(false));
    }
  };

  if (alarmData && seconds <= 0) return null;
  return (
    <SContainer
      onClick={onBannerClick}
      className={className}
      isDeactivated={isDeactivated}
      role="lwtBanner"
    >
      <div className="row" role="bannerInfoContainer">
        <img
          role="icon"
          src={isDeactivated ? InfoCircleWhite : InfoCircleDark}
          className="alarmIcon"
        />
        <p>{translate(isDeactivated ? 'alarm_deactivated_success' : 'alarm_banner_active_text')}</p>
      </div>
      <div className="row" role="bannerActionContainer">
        {!isDeactivated && !isLoading && <p role="timeText">{convertSecondsToTime(seconds)}</p>}
        <img role="icon" onClick={onCloseClick} src={isDeactivated ? CloseWhite : ArrowRightImg} />
      </div>
    </SContainer>
  );
};
